// src/data/resetTools.js
//
// Reset & Recharge tools we OWN: short, guided, text-cued and timed — no
// third-party video. Two kinds:
//   breath — a pacer: phases [{ label, secs, scale, hold? }] repeated `rounds` times
//   guided — a sequence of timed steps [{ secs, cue, detail }]
// `intent` is the calm, no-numbers tag shown on the card. `minutes` is approximate.

export const RESET_TOOLS = [
    {
        id: 'sigh', title: 'Physiological Sigh', minutes: 2, kind: 'breath', intent: 'Release',
        blurb: 'Two short breaths in through the nose, one long breath out. The quickest known way to bring the system down a notch.',
        phases: [
            { label: 'Inhale', secs: 2, scale: 1.35 },
            { label: 'Inhale a little more', secs: 1, scale: 1.5 },
            { label: 'Long exhale', secs: 6, scale: 1 }
        ],
        rounds: 13
    },
    {
        id: 'box', title: 'Box Breathing', minutes: 4, kind: 'breath', intent: 'Steady',
        blurb: 'Four seconds in, four to hold, four out, four to hold. A simple rhythm to steady the system.',
        phases: [
            { label: 'Inhale', secs: 4, scale: 1.5 },
            { label: 'Hold', secs: 4, scale: 1.5, hold: true },
            { label: 'Exhale', secs: 4, scale: 1 },
            { label: 'Hold', secs: 4, scale: 1, hold: true }
        ],
        rounds: 15
    },
    {
        id: 'grounding', title: '5-4-3-2-1 Grounding', minutes: 3, kind: 'guided', intent: 'Present',
        blurb: 'Come back to the room through your senses. Useful when your head is racing or after a knock.',
        steps: [
            { secs: 40, cue: '5 things you can see', detail: 'Name them slowly. Colours, edges, light.' },
            { secs: 35, cue: '4 things you can feel', detail: 'Feet on the floor, fabric, air on your skin.' },
            { secs: 30, cue: '3 things you can hear', detail: 'Near first, then far.' },
            { secs: 25, cue: '2 things you can smell', detail: 'Or two smells you like.' },
            { secs: 20, cue: '1 thing you can taste', detail: 'Or one slow breath, tasting the air.' },
            { secs: 20, cue: 'Back in the room', detail: 'Notice what changed.' }
        ]
    },
    {
        id: 'reset2', title: 'Two-Minute Reset', minutes: 2, kind: 'guided', intent: 'Sharpen',
        blurb: 'Breath, cue word, posture, first action — the reset routine from the Focus sheet, guided.',
        steps: [
            { secs: 30, cue: 'Breathe out longer than in', detail: 'Three times. Let the shoulders drop on each exhale.' },
            { secs: 30, cue: 'Say your cue word', detail: 'Quietly. If you don\'t have one yet, use "here".' },
            { secs: 30, cue: 'Set your posture', detail: 'Feet planted, chest open, eyes on one point.' },
            { secs: 30, cue: 'Pick your first action', detail: 'One thing. When the timer ends, go do it.' }
        ]
    },
    {
        id: 'bodyscan', title: 'Body Scan', minutes: 10, kind: 'guided', intent: 'Restore',
        blurb: 'Ten minutes lying down, moving attention through the body. Deep rest without sleep.',
        steps: [
            { secs: 60, cue: 'Settle', detail: 'Lie down. Let the floor take your weight. Breathe normally.' },
            { secs: 90, cue: 'Feet and legs', detail: 'Notice weight, warmth, contact. No need to change anything.' },
            { secs: 90, cue: 'Hips and lower back', detail: 'Let them widen and soften on each exhale.' },
            { secs: 90, cue: 'Belly and chest', detail: 'Feel the breath move them. Slow it a little.' },
            { secs: 90, cue: 'Hands, arms, shoulders', detail: 'Heavy. Let the shoulders fall away from the ears.' },
            { secs: 90, cue: 'Jaw, eyes, forehead', detail: 'Unclench the jaw. Let the eyes rest back.' },
            { secs: 60, cue: 'Whole body', detail: 'Everything at once, breathing.' },
            { secs: 30, cue: 'Come back', detail: 'Wiggle fingers and toes. Open your eyes when you\'re ready.' }
        ]
    }
];

export const getResetTool = (id) => RESET_TOOLS.find(t => t.id === id) || null;

export const toolDurationSecs = (tool) => tool.kind === 'breath'
    ? tool.phases.reduce((s, p) => s + p.secs, 0) * (tool.rounds || 1)
    : tool.steps.reduce((s, p) => s + p.secs, 0);

// Energy check-in (1-10) -> which tools to suggest, most fitting first.
export const suggestForEnergy = (energy) => {
    if (energy == null) return ['sigh', 'box'];
    if (energy <= 3) return ['bodyscan', 'sigh'];
    if (energy <= 6) return ['grounding', 'sigh'];
    return ['reset2', 'box'];
};

export const suggestionCopy = (energy) => {
    if (energy == null) return '';
    if (energy <= 3) return 'Low tank. Nothing to push through — a proper rest is the productive move.';
    if (energy <= 6) return 'Middling. A few minutes to settle will get more back than grinding on.';
    return 'Good tank. A quick sharpen and you\'re ready to go.';
};
